import { useState, useEffect, useRef, useCallback } from 'react';
import { subscribeSharedStream, getSharedStreamStatus } from '@/lib/shared-ctrader-stream';
import { ctraderPriceBus } from '@/lib/ctrader-price-bus';

interface SpotEventData {
  type: 'spot';
  symbolId: number | null;
  bid: number | null;
  ask: number | null;
  bidRaw: number | null;
  askRaw: number | null;
  timestamp: number;
}

interface TrendbarEventData {
  type: 'trendbar';
  symbolId: number | null;
  period: number | null;
  periodName: string | null;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
  timestamp: number | null; // trendbar time (utcTimestampInMinutes or unix ms)
  receivedAt: number;
}

interface ExecutionEventData {
  type: 'execution';
  payload: Record<string, unknown>;
  timestamp: number;
}

interface ErrorEventData {
  type: 'error';
  message?: string;
  errorCode?: unknown;
  description?: string;
}

interface ConnectedEventData {
  type: 'connected';
  accountId: number;
  subscribedSymbols: number[];
  subscribedPeriods: number[];
}

interface DisconnectedEventData {
  type: 'disconnected';
  code: number;
  reason: string;
}

interface TraderUpdateEventData {
  type: 'trader_update';
  payload: Record<string, unknown>;
  timestamp: number;
}

export type StreamEvent =
  | SpotEventData
  | TrendbarEventData
  | ExecutionEventData
  | TraderUpdateEventData
  | ErrorEventData
  | ConnectedEventData
  | DisconnectedEventData;

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

interface UseCtraderStreamOptions {
  accountId: number | null;
  symbolIds: number[];
  periods: number[]; // e.g. [60, 240] for H1 and H4
  symbolDigits?: Record<number, number>; // symbolId → digits for price scaling
  enabled?: boolean;
}

export function useCtraderStream({ accountId, symbolIds, periods, symbolDigits = {}, enabled = true }: UseCtraderStreamOptions) {
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [spotPrices, setSpotPrices] = useState<Record<number, { bid: number | null; ask: number | null; timestamp: number }>>({});
  const [trendbarEvents, setTrendbarEvents] = useState<TrendbarEventData[]>([]);
  const [executionEvents, setExecutionEvents] = useState<ExecutionEventData[]>([]);
  const [traderUpdateEvents, setTraderUpdateEvents] = useState<TraderUpdateEventData[]>([]);
  const [error, setError] = useState<string | null>(null);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const consumerIdRef = useRef<string>('');
  const mountedRef = useRef(true);
  const symbolIdsRef = useRef(symbolIds);
  const periodsRef = useRef(periods);
  const accountIdRef = useRef(accountId);
  const symbolDigitsRef = useRef(symbolDigits);

  // Keep refs in sync
  useEffect(() => { symbolIdsRef.current = symbolIds; }, [symbolIds]);
  useEffect(() => { periodsRef.current = periods; }, [periods]);
  useEffect(() => { accountIdRef.current = accountId; }, [accountId]);
  useEffect(() => { symbolDigitsRef.current = symbolDigits; }, [symbolDigits]);

  const connect = useCallback(() => {
    if (!accountId || !mountedRef.current) return;

    // Unsubscribe previous
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (mountedRef.current) {
      setConnectionState('connecting');
      setError(null);
    }

    const consumerId = consumerIdRef.current || `ui__${accountId}`;
    consumerIdRef.current = consumerId;

    unsubscribeRef.current = subscribeSharedStream(
      {
        id: consumerId,
        symbolIds: symbolIdsRef.current,
        periods: periodsRef.current,
        symbolDigits: symbolDigitsRef.current,
        onEvent: (evt: StreamEvent) => {
          if (!mountedRef.current) return;

          switch (evt.type) {
            case 'spot': {
              if (evt.symbolId !== null && evt.symbolId !== undefined) {
                setSpotPrices((prev) => ({
                  ...prev,
                  [evt.symbolId!]: {
                    bid: evt.bid ?? null,
                    ask: evt.ask ?? null,
                    timestamp: evt.receivedAt || Date.now(),
                  },
                }));
                ctraderPriceBus.publish({
                  symbolId: evt.symbolId,
                  bid: evt.bid ?? null,
                  ask: evt.ask ?? null,
                  timestamp: evt.receivedAt || Date.now(),
                });
              }
              break;
            }
            case 'trendbar': {
              setTrendbarEvents((prev) => [...prev.slice(-499), {
                type: 'trendbar',
                symbolId: evt.symbolId ?? null,
                period: evt.period ?? null,
                periodName: null,
                open: evt.open ?? null,
                high: evt.high ?? null,
                low: evt.low ?? null,
                close: evt.close ?? null,
                volume: evt.volume ?? null,
                timestamp: evt.timestamp ?? null,
                receivedAt: evt.receivedAt || Date.now(),
              }]);
              break;
            }
            case 'execution': {
              setExecutionEvents((prev) => [...prev.slice(-99), {
                type: 'execution',
                payload: evt.payload || {},
                timestamp: evt.receivedAt || Date.now(),
              }]);
              break;
            }
            case 'trader_update': {
              setTraderUpdateEvents((prev) => [...prev.slice(-49), {
                type: 'trader_update',
                payload: evt.payload || {},
                timestamp: evt.receivedAt || Date.now(),
              }]);
              break;
            }
            case 'connected': {
              if (mountedRef.current) setConnectionState('connected');
              break;
            }
            case 'error': {
              if (mountedRef.current) {
                setError(evt.description || evt.message || 'Unknown stream error');
                setConnectionState('error');
              }
              break;
            }
            case 'disconnected': {
              if (mountedRef.current) setConnectionState('disconnected');
              break;
            }
          }
        },
        onStatusChange: (status) => {
          if (!mountedRef.current) return;
          setConnectionState(status);
          if (status === 'connected') {
            setTrendbarEvents([]);
            setExecutionEvents([]);
          }
          if (status === 'error') {
            // Don't overwrite a more specific error from onEvent
          }
        },
      },
      accountId,
    );
  }, [accountId]);

  // Auto-reconnect: if shared stream has other consumers, it auto-reconnects itself.
  // We poll for status updates, and only force a reconnect after 5s if still down.
  useEffect(() => {
    if (connectionState === 'disconnected' && enabled && accountId && mountedRef.current) {
      // Poll shared stream status every second
      const pollTimer = setInterval(() => {
        if (!mountedRef.current) return;
        const sharedStatus = getSharedStreamStatus(accountId);
        if (sharedStatus !== 'disconnected' && sharedStatus !== connectionState) {
          setConnectionState(sharedStatus);
        }
      }, 1000);

      // Only force reconnect after 5s if shared stream is still disconnected
      const reconnectTimer = setTimeout(() => {
        if (mountedRef.current && getSharedStreamStatus(accountId) === 'disconnected') {
          connect();
        }
      }, 5000);

      return () => {
        clearInterval(pollTimer);
        clearTimeout(reconnectTimer);
      };
    }
  }, [connectionState, enabled, accountId, connect]);

  // Start/stop streaming
  useEffect(() => {
    mountedRef.current = true;

    if (enabled && accountId && symbolIds.length > 0) {
      connect();
    } else {
      // Clean up subscription
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (mountedRef.current) {
        setConnectionState('idle');
        setSpotPrices({});
        setTrendbarEvents([]);
        setExecutionEvents([]);
        setTraderUpdateEvents([]);
        setError(null);
      }
    }

    return () => {
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [enabled, accountId, symbolIds.length > 0, connect]);

  const stop = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (mountedRef.current) {
      setConnectionState('idle');
      setSpotPrices({});
      setTrendbarEvents([]);
      setExecutionEvents([]);
      setTraderUpdateEvents([]);
      setError(null);
    }
  }, []);

  return {
    connectionState,
    spotPrices,
    trendbarEvents,
    executionEvents,
    traderUpdateEvents,
    error,
    stop,
    connect,
  };
}